import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Video, Calendar as CalendarIcon, CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ActionCard } from "@/components/ActionCard";
import { Calendar } from "@/components/Calendar";
import { StartLiveCallModal } from "@/components/StartLiveCallModal";
import { ScheduleCallModal } from "@/components/ScheduleCallModal";
import { RequestRecordingModal } from "@/components/RequestRecordingModal";
import { TrialWelcomeModal } from "@/components/TrialWelcomeModal";
import { ReferralWelcomeModal } from "@/components/ReferralWelcomeModal";
import { LaunchDayModal } from "@/components/LaunchDayModal";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { useToast } from "@/shared/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { SubscriptionButton } from "@/shared/components/SubscriptionButton";
import { useExtensionHcpPrefill } from "@/shared/hooks/useExtensionHcpPrefill";
import { useAuth } from "@/shared/contexts/AuthContext";
import { IntakeLinkCard } from "@/features/intake/components/IntakeLinkCard";

const Index = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { usage, loading: loadingSubscription } = useSubscription();

  const [showStartCallModal, setShowStartCallModal] = useState(false);
  const [showScheduleCallModal, setShowScheduleCallModal] = useState(false);
  const [showRequestRecordingModal, setShowRequestRecordingModal] = useState(false);
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [showLaunchDayModal, setShowLaunchDayModal] = useState(false);
  const [showTrialWelcome, setShowTrialWelcome] = useState(false);
  const [showReferralWelcome, setShowReferralWelcome] = useState(false);

  const extensionHcpPrefill = useExtensionHcpPrefill();
  const { user } = useAuth();

  // Check if user has access - matches mobile app behavior
  const hasAccess = !loadingSubscription && usage && (
    usage.subscribed ||
    (usage.isTrialActive && usage.trialType === 'app')
  );

  // Check if user should see launch day, trial, or referral modal
  useEffect(() => {
    const checkShouldShowModal = async () => {
      try {
        // CRITICAL FIX: Use localStorage instead of getUser() to prevent hanging
        const { getUserId, getSupabaseSession } = await import('@/shared/utils/supabaseStorage');
        const userId = getUserId();
        const session = getSupabaseSession();

        if (!userId || !session?.user) return;

        const user = session.user;

        // Wait for subscription data to load
        if (loadingSubscription) return;

        // PRIORITY 0: Launch Day Modal - January 5, 2025 ONLY (for non-subscribed users)
        const today = new Date();
        const isLaunchDay = today.getFullYear() === 2025 &&
                           today.getMonth() === 0 && // January is 0
                           today.getDate() === 5;

        if (isLaunchDay && !usage?.subscribed) {
          const LAUNCH_DAY_MODAL_SHOWN_KEY = `launch_day_modal_shown_${user.id}`;
          const hasShownLaunchDayModal = localStorage.getItem(LAUNCH_DAY_MODAL_SHOWN_KEY);

          if (!hasShownLaunchDayModal) {
            console.log('[Dashboard] 🎉 LAUNCH DAY - Showing LaunchDayModal');
            setShowLaunchDayModal(true);
            return; // Don't show other modals if showing launch day modal
          }
        }

        // Check if user is referred
        const referralCode = user?.user_metadata?.referral_code || localStorage.getItem('pendingReferralCode');
        const isReferredUser = !!referralCode || !!usage?.referral_stripe_coupon_id;

        console.log('[Dashboard] Modal check:', {
          isTrialActive: usage?.isTrialActive,
          subscribed: usage?.subscribed,
          isReferredUser,
          referralCode,
          hasReferralCoupon: !!usage?.referral_stripe_coupon_id
        });

        // PRIORITY 1: Show ReferralWelcomeModal for referred users (ONCE)
        if (isReferredUser && !usage?.subscribed) {
          const REFERRAL_MODAL_SHOWN_KEY = `referral_modal_shown_${user.id}`;
          const hasShownReferralModal = localStorage.getItem(REFERRAL_MODAL_SHOWN_KEY);

          if (!hasShownReferralModal) {
            console.log('[Dashboard] ✅ REFERRED USER - Showing ReferralWelcomeModal (no API call)');

            // Show modal immediately - no API call to fetch referrer info
            // Modal will show generic "You've been referred" message
            setShowReferralWelcome(true);

            return; // Don't show trial modal if showing referral modal
          }
        }

        // PRIORITY 2: Show TrialWelcomeModal for free trial users (DAILY)
        const shouldShowTrial =
          usage?.isTrialActive &&     // User is in an active trial
          !usage?.subscribed &&       // User has not subscribed (free trial, not paid trial)
          !isReferredUser;            // User was NOT referred

        if (!shouldShowTrial) return;

        // Check if user has seen it today (matching mobile's daily logic)
        const TRIAL_MODAL_SHOWN_KEY = 'trial_modal_last_shown';
        const todayStr = new Date().toDateString(); // e.g., "Wed Dec 25 2025"
        const storageKey = `${TRIAL_MODAL_SHOWN_KEY}_${user.id}`;
        const lastShownDate = localStorage.getItem(storageKey);

        if (lastShownDate !== todayStr) {
          console.log('[TrialModal] ✅ Showing modal - last shown:', lastShownDate, 'today:', todayStr);
          // Show modal immediately (reduced from 500ms to prevent click-through)
          // Note: We DON'T set localStorage here - only when modal actually opens
          setShowTrialWelcome(true);
        } else {
          console.log('[TrialModal] ⏭️ Already shown today');
        }
      } catch (error) {
        console.error('[Dashboard] ❌ Modal check error:', error);
      }
    };

    checkShouldShowModal();
  }, [loadingSubscription, usage?.isTrialActive, usage?.subscribed, usage?.referral_stripe_coupon_id]); // Re-run when subscription data changes

  const handleLaunchDayModalClose = () => {
    setShowLaunchDayModal(false);
  };

  const handleTrialWelcomeClose = () => {
    setShowTrialWelcome(false);
    // Note: We don't clear the date tracking - it will reset tomorrow automatically
  };

  const handleReferralWelcomeClose = () => {
    setShowReferralWelcome(false);
  };

  const handleStartLiveCall = () => {
    console.log("StartCallModal line 19")
    if (!hasAccess) {
      setShowSubscribeDialog(true);
      return;
    }

    // FIXED: Check for paused/canceled subscription like mobile does
    if (usage && (usage.is_paused || usage.subscription_status === 'canceled')) {
      toast({
        title: "Subscription Required",
        description: `Your subscription is ${usage.is_paused ? 'paused' : 'canceled'}. Please reactivate to access this feature.`,
        variant: "destructive",
      });
      navigate('/profile?tab=billing');
      return;
    }

    setShowStartCallModal(true);
  };

  const handleScheduleCall = () => {
    if (!hasAccess) {
      setShowSubscribeDialog(true);
      return;
    }

    // FIXED: Check for paused/canceled subscription like mobile does
    if (usage && (usage.is_paused || usage.subscription_status === 'canceled')) {
      toast({
        title: "Subscription Required",
        description: `Your subscription is ${usage.is_paused ? 'paused' : 'canceled'}. Please reactivate to access this feature.`,
        variant: "destructive",
      });
      navigate('/profile?tab=billing');
      return;
    }

    setShowScheduleCallModal(true);
  };

  const handleRequestRecording = () => {
    if (!hasAccess) {
      setShowSubscribeDialog(true);
      return;
    }

    // FIXED: Check for paused/canceled subscription like mobile does
    if (usage && (usage.is_paused || usage.subscription_status === 'canceled')) {
      toast({
        title: "Subscription Required",
        description: `Your subscription is ${usage.is_paused ? 'paused' : 'canceled'}. Please reactivate to access this feature.`,
        variant: "destructive",
      });
      navigate('/profile?tab=billing');
      return;
    }

    setShowRequestRecordingModal(true);
  };

  const handleSubscribe = () => {
    setShowSubscribeDialog(false);
    navigate('/profile?tab=billing');
  };

  return (
    <div className="p-1 md:p-6 space-y-2 md:space-y-8 h-full overflow-y-auto overflow-x-hidden">
      {/* Welcome Section */}
      <div className="space-y-1 md:space-y-2 text-center md:text-left">
        <h1 className="text-sm md:text-3xl font-bold text-foreground">{t("home.dashboard")}</h1>
        <p className="text-xs md:text-lg text-muted-foreground">
          {t("home.welcomeMessage")}
        </p>
      </div>

      {/* Action Cards */}
      <div className="w-[65%] mx-auto md:w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1 md:gap-6">
        <ActionCard
          title={t("home.startLiveCall")}
          description={t("home.startLiveCallDesc")}
          buttonText={t("home.startNow")}
          icon={Video}
          variant="primary"
          onClick={handleStartLiveCall}
        />
        
        <ActionCard
          title={t("home.scheduleCall")}
          description={t("home.scheduleCallDesc")}
          buttonText={t("home.schedule")}
          icon={CalendarIcon}
          variant="secondary"
          onClick={handleScheduleCall}
        />
        
        <ActionCard
          title={t("home.requestRecording")}
          description={t("home.requestRecordingDesc")}
          buttonText={t("home.request")}
          iconSrc="/lovable-uploads/f0d521fe-ed76-42b6-a34b-790351404f71.png"
          variant="accent"
          onClick={handleRequestRecording}
        />
        </div>
      </div>

      {/* Public Intake Link */}
      {user?.id && (
        <div className="w-[65%] mx-auto md:w-full">
          <IntakeLinkCard contractorId={user.id} />
        </div>
      )}

      {/* Calendar */}
      <div className="mt-4 md:mt-8">
        <Calendar />
      </div>

      {/* Modals */}
      <StartLiveCallModal
        open={showStartCallModal}
        onOpenChange={setShowStartCallModal}
        prefillData={extensionHcpPrefill}
      />
      <ScheduleCallModal
        open={showScheduleCallModal}
        onOpenChange={setShowScheduleCallModal}
        prefillData={extensionHcpPrefill}
      />
      <RequestRecordingModal
        open={showRequestRecordingModal}
        onOpenChange={setShowRequestRecordingModal}
        prefillData={extensionHcpPrefill}
      />

      {/* Launch Day Modal - Jan 5, 2025 ONLY */}
      <LaunchDayModal
        open={showLaunchDayModal}
        onOpenChange={handleLaunchDayModalClose}
      />

      {/* Referral Welcome Modal - Shown to referred users (no referrer name) */}
      <ReferralWelcomeModal
        open={showReferralWelcome}
        onClose={handleReferralWelcomeClose}
        discountAmount={100}
        discountMonths={3}
      />

      {/* Trial Welcome Modal - Shown to new users */}
      <TrialWelcomeModal
        open={showTrialWelcome}
        onOpenChange={handleTrialWelcomeClose}
        trialDays={15}
      />

      {/* Subscribe Dialog - Matching mobile app behavior */}
      <Dialog open={showSubscribeDialog} onOpenChange={setShowSubscribeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <DialogTitle>Subscribe to Continue</DialogTitle>
            </div>
            <DialogDescription>
              Subscribe to a plan to access all features including video calls, scheduling, and recording requests.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium">What you'll get:</p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• Start live video calls with customers</li>
                <li>• Schedule video calls in advance</li>
                <li>• Request video recordings</li>
                <li>• Access to all premium features</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowSubscribeDialog(false)}
              >
                Cancel
              </Button>
              <SubscriptionButton
                className="flex-1"
                onClick={handleSubscribe}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Index;
